import {
  TokenType,
  type Token,
  type SourceLocation,
  type SourceRange,
  type Trivia,
} from "../types/tokens.js";
import type {
  StyleSheet,
  StyleRule,
  StyleSelector,
  DeclarationTrivia,
  ValueNode,
} from "../types/style.js";
import type { Diagnostic, ParseResult } from "../types/ast.js";
import { StyleLexer } from "../lexer/style-lexer.js";

const ANONYMOUS_SHEET_ID = "<anonymous>";

export class StyleParser {
  private tokens: Token[];
  private pos = 0;
  private diagnostics: Diagnostic[] = [];
  private ruleIndex = 0;
  private sheetId: string;
  /**
   * Stamped onto every range the parser builds; see {@link SourceRange.file}.
   * Trivia ranges come from the lexer and carry none: nothing anchors a
   * diagnostic on a comment.
   */
  private readonly filePath: string | undefined;

  constructor(tokens: Token[], sheetId: string = ANONYMOUS_SHEET_ID, filePath?: string) {
    this.tokens = tokens;
    this.sheetId = sheetId;
    this.filePath = filePath;
  }

  /**
   * @param filePath The sheet's absolute path, when it has one. Kept apart from
   *   `sheetId` because a sheet id is not always a path: the built-in sheets
   *   pass a synthetic id, and stamping that as a file would name a document
   *   that does not exist (#2715).
   */
  static parse(
    source: string,
    sheetId: string = ANONYMOUS_SHEET_ID,
    filePath?: string,
  ): ParseResult<StyleSheet> {
    const tokens = new StyleLexer(source).tokenize();
    const parser = new StyleParser(tokens, sheetId, filePath);
    return parser.parseStyleSheet();
  }

  private peek(): Token {
    return (
      this.tokens[this.pos] ?? {
        type: TokenType.EOF,
        value: "",
        loc: { line: 0, column: 0, offset: 0 },
      }
    );
  }

  private peekAt(offset: number): Token {
    const idx = this.pos + offset;
    if (idx < 0 || idx >= this.tokens.length) {
      return {
        type: TokenType.EOF,
        value: "",
        loc: { line: 0, column: 0, offset: 0 },
      };
    }
    return this.tokens[idx];
  }

  private advance(): Token {
    const token = this.peek();
    this.pos++;
    return token;
  }

  private expect(type: TokenType): Token {
    const token = this.peek();
    if (token.type !== type) {
      this.diagnostics.push({
        severity: "error",
        code: "style-token-type-mismatch",
        params: { expected: String(type), got: String(token.type), value: token.value },
        loc: this.rangeBetween(token, token),
      });
      return token;
    }
    return this.advance();
  }

  private match(type: TokenType): boolean {
    if (this.peek().type === type) {
      this.advance();
      return true;
    }
    return false;
  }

  private rangeBetween(start: Token, end: Token): SourceRange {
    return inFile({ start: { ...start.loc }, end: this.endOfToken(end) }, this.filePath);
  }

  private endOfToken(token: Token): SourceLocation {
    const len = token.value.length;
    return {
      line: token.loc.line,
      column: token.loc.column + len,
      offset: token.loc.offset + len,
    };
  }

  parseStyleSheet(): ParseResult<StyleSheet> {
    const rules: StyleRule[] = [];

    while (this.peek().type !== TokenType.EOF) {
      const parsedRules = this.parseRuleSet();
      rules.push(...parsedRules);
    }

    // Anything still attached to the EOF token (or trailing trivia of the
    // last rule's closing `}`) belongs at the sheet trailing trivia. The
    // last rule's `trailingTrivia` already absorbed same-line items; the
    // remainder is multi-line trivia that follows the file's last
    // construct.
    const trailingTrivia = takeLeadingTrivia(this.peek());

    return {
      value: { rules, sheetId: this.sheetId, trailingTrivia },
      diagnostics: this.diagnostics,
    };
  }

  private parseRuleSet(): StyleRule[] {
    const startToken = this.peek();
    const leadingTrivia = takeLeadingTrivia(startToken);
    const selectors = this.parseSelectorList();
    this.expect(TokenType.LeftBrace);

    const properties: Record<string, string> = {};
    const declarationLocs: Record<string, SourceRange> = {};
    const declarationTrivia: Record<string, DeclarationTrivia> = {};
    const valueNodes: Record<string, ValueNode> = {};
    let lastDeclarationName: string | null = null;
    while (this.peek().type !== TokenType.RightBrace && this.peek().type !== TokenType.EOF) {
      const beforePos = this.pos;
      const declName = this.parseDeclaration(
        properties,
        declarationLocs,
        declarationTrivia,
        valueNodes,
        lastDeclarationName,
      );
      if (declName) {
        lastDeclarationName = declName;
      } else if (this.pos === beforePos) {
        // Defensive: parseDeclaration always advances, but make sure we
        // never spin if it does not.
        this.advance();
      }
    }
    const closeToken = this.peek();
    // Trivia between the last `;` (or last decl tail) and `}`. Attach it to
    // the last declaration's trailing so comments tucked at end of rule
    // survive a Tidy round-trip.
    const beforeBrace = takeLeadingTrivia(closeToken);
    if (beforeBrace.length > 0 && lastDeclarationName) {
      const decl = declarationTrivia[lastDeclarationName] ?? { leading: [], trailing: [] };
      declarationTrivia[lastDeclarationName] = {
        leading: decl.leading,
        trailing: [...decl.trailing, ...beforeBrace],
      };
    }
    this.expect(TokenType.RightBrace);

    // Trivia immediately following `}`: split into same-line trailing of
    // this rule vs. multi-line leading of the next construct.
    const afterBrace = splitSameLineTrivia(this.peek(), closeToken.loc.line);
    const trailingTrivia = afterBrace.sameLine;

    const ruleLoc = this.rangeBetween(startToken, closeToken);
    return selectors.map((selector, idx) => ({
      selector,
      properties: { ...properties },
      specificity: computeSpecificity(selector),
      sourceIndex: this.ruleIndex++,
      loc: ruleLoc,
      declarationLocs: { ...declarationLocs },
      sheetId: this.sheetId,
      // Attach trivia only to the first emitted rule when a comma-list
      // expanded into multiple rules; subsequent rules get empty trivia
      // so the reformatter does not duplicate comments.
      leadingTrivia: idx === 0 ? leadingTrivia : [],
      trailingTrivia: idx === selectors.length - 1 ? trailingTrivia : [],
      declarationTrivia: idx === 0 ? { ...declarationTrivia } : {},
      valueNodes: { ...valueNodes },
    }));
  }

  private parseSelectorList(): StyleSelector[] {
    const selectors: StyleSelector[] = [];
    selectors.push(this.parseSelector());

    while (this.peek().type === TokenType.Comma) {
      this.advance(); // ,
      selectors.push(this.parseSelector());
    }

    return selectors;
  }

  private parseSelector(): StyleSelector {
    const startToken = this.peek();
    const selector: StyleSelector = {
      tags: [],
      annotations: [],
      facets: [],
      loc: this.rangeBetween(startToken, startToken),
    };

    // #id selector
    if (this.peek().type === TokenType.Hash) {
      this.advance(); // #
      const idToken = this.expect(TokenType.Identifier);
      selector.id = idToken.value;
      selector.loc = this.rangeBetween(startToken, idToken);
      return selector;
    }

    let lastToken = startToken;

    // Type selector (identifier like "service", "edge", "user")
    if (this.peek().type === TokenType.Identifier) {
      lastToken = this.advance();
      selector.nodeType = lastToken.value;
    }

    // boundary#<id> selector (#2234) — only meaningful after `boundary`, the
    // same shape as edge#<id> below. A boundary is not a node, so a bare
    // `#<id>` would address the node id space instead; the keyword is what
    // selects which space the id is read in.
    if (selector.nodeType === "boundary" && this.peek().type === TokenType.Hash) {
      this.advance(); // #
      const id = this.expect(TokenType.Identifier);
      selector.boundaryId = id.value;
      lastToken = id;
    }

    // team#<id> selector (#2269) — a *compound*, not a new id space. `team` is
    // a node kind in the org tree view and `#<id>` already addresses a node
    // there, so this narrows the existing match to the team kind rather than
    // moving it onto another object. It therefore lands in `selector.id`, which
    // is what makes the node matchers AND the two parts and what scores it 101
    // (100 for the id + 1 for the kind) with no change to `computeSpecificity`.
    if (selector.nodeType === "team" && this.peek().type === TokenType.Hash) {
      this.advance(); // #
      const id = this.expect(TokenType.Identifier);
      selector.id = id.value;
      lastToken = id;
    }

    // edge#<id> selector — only meaningful after `edge`. Accepts either an
    // author identifier (`edge#criticalWrite`) or a base form
    // (`edge#A->B` / `edge#A-->B`).
    if (selector.nodeType === "edge" && this.peek().type === TokenType.Hash) {
      this.advance(); // #
      const first = this.expect(TokenType.Identifier);
      const next = this.peek().type;
      if (next === TokenType.Arrow || next === TokenType.DashedArrow) {
        const arrow = this.advance().value;
        const second = this.expect(TokenType.Identifier);
        selector.edgeId = `${first.value}${arrow}${second.value}`;
        lastToken = second;
      } else {
        selector.edgeId = first.value;
        lastToken = first;
      }
    }

    // Tag selectors `[tag]`, edge endpoint selectors `[from=<id>]` /
    // `[to=<id>]`, and facet selectors `[facets=<id>]` (#2175). All share the
    // `[...]` bracket; an `=` after the inner identifier distinguishes an
    // attribute predicate from a plain tag (a tag is always `[ident]` with no
    // `=`, so this never shadows tag parsing).
    while (this.peek().type === TokenType.LeftBracket) {
      this.advance(); // [
      const inner = this.expect(TokenType.Identifier);
      if (this.peek().type === TokenType.Equals) {
        this.advance(); // =
        // `<id>` may be a dot-notation endpoint (e.g. `OrderDB.OrderTable`),
        // which the lexer already folds into a single Identifier.
        const value = this.expect(TokenType.Identifier);
        if (inner.value === "from") {
          selector.edgeFrom = value.value;
        } else if (inner.value === "to") {
          selector.edgeTo = value.value;
        } else if (inner.value === "facets") {
          // Repeatable and ANDed, like tags. Deliberately the same spelling as
          // the `.krs` property, so the sheet reads as "the thing I wrote over
          // there" rather than as a second vocabulary to learn.
          selector.facets.push(value.value);
        } else {
          this.diagnostics.push({
            severity: "error",
            code: "unknown-edge-selector-attribute",
            params: { attribute: inner.value },
            loc: this.rangeBetween(inner, inner),
          });
        }
        lastToken = this.expect(TokenType.RightBracket);
      } else {
        lastToken = this.expect(TokenType.RightBracket);
        selector.tags.push(inner.value);
      }
    }

    // Annotation selectors @annotation
    while (this.peek().type === TokenType.At) {
      this.advance(); // @
      lastToken = this.expect(TokenType.Identifier);
      selector.annotations.push(lastToken.value);
    }

    selector.loc = this.rangeBetween(startToken, lastToken);
    return selector;
  }

  private parseDeclaration(
    properties: Record<string, string>,
    declarationLocs: Record<string, SourceRange>,
    declarationTrivia: Record<string, DeclarationTrivia>,
    valueNodes: Record<string, ValueNode>,
    previousDeclarationName: string | null,
  ): string | null {
    if (this.peek().type !== TokenType.Identifier) {
      this.diagnostics.push({
        severity: "error",
        code: "expected-style-property-name",
        params: { got: String(this.peek().type) },
        loc: this.rangeBetween(this.peek(), this.peek()),
      });
      this.advance();
      return null;
    }

    const propertyToken = this.peek();
    const leading = takeLeadingTrivia(propertyToken);
    this.advance();
    const property = propertyToken.value;
    this.expect(TokenType.Colon);

    const valueResult = this.parseValue(property);
    // A refused value has been reported; the declaration takes no effect.
    if (!valueResult.refused) {
      properties[property] = valueResult.text;
      if (valueResult.node) {
        valueNodes[property] = valueResult.node;
      }
    }

    const semicolonToken = this.peek().type === TokenType.Semicolon ? this.peek() : null;
    this.match(TokenType.Semicolon);
    declarationLocs[property] = this.rangeBetween(propertyToken, semicolonToken ?? this.peekAt(-1));

    // Trivia leading the *next* token may include line-comments that share
    // the same line as our `;` — those become this declaration's trailing.
    const boundaryLine = (semicolonToken ?? this.peekAt(-1)).loc.line;
    const split = splitSameLineTrivia(this.peek(), boundaryLine);

    declarationTrivia[property] = {
      leading,
      trailing: split.sameLine,
    };

    // Defensive — keep any pre-existing entry for the previous declaration
    // intact (they were already finalized when we parsed them).
    void previousDeclarationName;

    return property;
  }

  /**
   * Parse a property value into both its joined-string form (canonical for
   * resolver / Tidy / svg-builder) and a structured `ValueNode` (used by
   * the validator pass added in Phase 3 / PR-B). Both consume the same
   * tokens, so they are produced together to avoid re-parsing.
   *
   * A value the parser cannot hold whole is refused (`refused: true`) and
   * reported here, once, as `style-token-type-mismatch`: a token no value
   * can contain, an empty value, or an empty comma item. The caller then
   * stores nothing, so no later pass sees a value with a piece missing
   * (#3140, #3141). Every value that is not refused has a `node`.
   */
  private parseValue(propertyName: string): {
    text: string;
    node: ValueNode | undefined;
    refused: boolean;
  } {
    const parts: string[] = [];
    let refused = false;
    const atoms: ValueNode[] = [];
    const segmentStarts: number[] = [0]; // indices into `atoms` where each comma-separated segment starts
    let lastComma: Token | null = null;
    let recovered = false;

    while (
      this.peek().type !== TokenType.Semicolon &&
      this.peek().type !== TokenType.RightBrace &&
      this.peek().type !== TokenType.EOF
    ) {
      const token = this.peek();
      if (token.type === TokenType.StringLiteral) {
        const t = this.advance();
        parts.push(`"${t.value}"`);
        atoms.push({
          kind: "string",
          value: t.value,
          loc: tokenLoc(t, this.filePath),
        });
      } else if (token.type === TokenType.Identifier) {
        const t = this.advance();
        const ident = t.value;
        // Function call like url("...")
        if (this.peek().type === TokenType.LeftParen) {
          this.advance(); // (
          let arg = "";
          while (this.peek().type !== TokenType.RightParen && this.peek().type !== TokenType.EOF) {
            if (this.peek().type === TokenType.Unknown) {
              // `url(😀A)`: refused like an Unknown token outside the parens.
              refused = this.refuse(this.peek(), refused);
              arg += this.advance().value;
            } else if (this.peek().type === TokenType.StringLiteral) {
              arg = this.advance().value;
            } else {
              arg += this.advance().value;
            }
          }
          const close = this.peek().type === TokenType.RightParen ? this.peek() : null;
          this.expect(TokenType.RightParen);
          parts.push(`${ident}("${arg}")`);
          atoms.push({
            kind: "function",
            name: ident,
            argRaw: arg,
            loc: rangeBetweenTokens(t, close ?? t, this.filePath),
          });
        } else {
          parts.push(ident);
          atoms.push(classifyIdentAtom(ident, t, this.filePath));
        }
      } else if (token.type === TokenType.Comma) {
        // Recovery for "comma instead of semicolon" (#1168): if the
        // comma is immediately followed by `<identifier> :`, the user
        // most likely meant `;`. Emit a diagnostic and treat the comma
        // as a terminator.
        if (
          this.peekAt(1).type === TokenType.Identifier &&
          this.peekAt(2).type === TokenType.Colon
        ) {
          this.diagnostics.push({
            severity: "error",
            code: "expected-semicolon-between-properties",
            params: { property: propertyName },
            loc: this.rangeBetween(token, token),
          });
          this.advance(); // consume the comma — treat as `;`
          recovered = true;
          break;
        }
        // Legitimate comma — start a new segment for the list builder. An
        // item with nothing in it (`a, , b`, `, a`) is refused: there is no
        // value to put in its place.
        if (atoms.length === segmentStarts[segmentStarts.length - 1]) {
          refused = this.refuse(token, refused);
        }
        lastComma = token;
        parts.push(this.advance().value);
        segmentStarts.push(atoms.length);
      } else if (token.type === TokenType.Hash && this.isAdjacentIdentifier(token)) {
        // `#zzzz`: the lexer reads only a well-formed hex as one token, so a
        // malformed color arrives as `#` + identifier. Keep it as the hex
        // atom the author meant, so the validator reports the color
        // (`style-invalid-hex-color`) rather than a stray `#`.
        this.advance();
        const t = this.advance();
        const value = `#${t.value}`;
        parts.push(value);
        atoms.push({ kind: "hex", value, loc: rangeBetweenTokens(token, t, this.filePath) });
      } else {
        // A token no value can hold (a stray `-`, an emoji, a bracket):
        // refuse the declaration. Keeping the rest would apply `- 20px` as
        // `20px` (#3140), or let a value the validator never saw reach the
        // resolver (#3141).
        refused = this.refuse(token, refused);
        parts.push(this.advance().value);
      }
    }

    if (!refused) {
      if (atoms.length === 0) {
        // `color: ;` — nothing to apply. After `color: , font-size: …` the
        // comma recovery has already reported the declaration.
        refused = this.refuse(this.peek(), recovered);
      } else if (lastComma && atoms.length === segmentStarts[segmentStarts.length - 1]) {
        // `font-family: serif,` — the last item is empty.
        refused = this.refuse(lastComma, refused);
      }
    }

    const text = parts.join(" ").trim();
    const node = refused ? undefined : buildValueNode(atoms, segmentStarts);
    return { text, node, refused };
  }

  /** Whether `hash` is immediately followed, with no space, by an identifier. */
  private isAdjacentIdentifier(hash: Token): boolean {
    const next = this.peekAt(1);
    return next.type === TokenType.Identifier && next.loc.offset === hash.loc.offset + 1;
  }

  /**
   * Refuse the declaration at `token`, reporting only the first reason: one
   * declaration that takes no effect is one finding, however many stray
   * tokens it holds. Returns the new `refused` state, always `true`.
   */
  private refuse(token: Token, alreadyRefused: boolean): true {
    if (!alreadyRefused) this.reportUnknownValueToken(token);
    return true;
  }

  /** A token no value can hold; the caller refuses the whole declaration. */
  private reportUnknownValueToken(token: Token): void {
    this.diagnostics.push({
      severity: "error",
      code: "style-token-type-mismatch",
      params: { expected: "Value", got: String(token.type), value: token.value },
      loc: this.rangeBetween(token, token),
    });
  }
}

/**
 * Classify a bare identifier atom. Hex tokens land here too because the
 * lexer emits them as `Identifier` with a leading `#` (see style-lexer
 * `readHexColor`). Numeric values (`12`, `12px`, `0.5`) similarly come
 * through as `Identifier`.
 */
function classifyIdentAtom(value: string, token: Token, file: string | undefined): ValueNode {
  if (value.startsWith("#")) {
    return { kind: "hex", value, loc: tokenLoc(token, file) };
  }
  const numericMatch = /^(-?(?:\d+(?:\.\d+)?|\.\d+))([a-zA-Z%]+)?$/.exec(value);
  if (numericMatch) {
    const numeric = Number.parseFloat(numericMatch[1]);
    const unit = numericMatch[2];
    if (unit) {
      return {
        kind: "length",
        value: numeric,
        unit,
        raw: numericMatch[1],
        loc: tokenLoc(token, file),
      };
    }
    return {
      kind: "number",
      value: numeric,
      raw: numericMatch[1],
      loc: tokenLoc(token, file),
    };
  }
  return { kind: "ident", value, loc: tokenLoc(token, file) };
}

/**
 * The value node for a declaration's atoms. Commas split items; within an
 * item, one atom stands for itself and several make a `sequence` (#3141), so
 * every atom the author wrote reaches the validator. The caller has already
 * refused an empty value or an empty item, so every segment has an atom.
 */
function buildValueNode(atoms: ValueNode[], segmentStarts: number[]): ValueNode {
  const items: ValueNode[] = [];
  for (let i = 0; i < segmentStarts.length; i++) {
    const end = i + 1 < segmentStarts.length ? segmentStarts[i + 1] : atoms.length;
    items.push(segmentNode(atoms.slice(segmentStarts[i], end)));
  }
  if (items.length === 1) return items[0];
  return {
    kind: "list",
    items,
    loc: spanOf(items),
  };
}

/** One comma item: a single atom, or a `sequence` of its space-separated atoms. */
function segmentNode(atoms: ValueNode[]): ValueNode {
  const [first, second, ...rest] = atoms;
  if (second === undefined) return first;
  return { kind: "sequence", items: [first, second, ...rest], loc: spanOf(atoms) };
}

/** From the first node's start to the last one's end. */
function spanOf(nodes: ValueNode[]): SourceRange {
  // The nodes are atoms of one declaration, so they share its file.
  return inFile(
    { start: nodes[0].loc.start, end: nodes[nodes.length - 1].loc.end },
    nodes[0].loc.file,
  );
}

/**
 * Attach the sheet's file to a range when the parse was given one, omitting
 * the key otherwise, so a sheet parsed without a path compares equal to the
 * shape it had before (#2715, TPL-2715). Every caller hands in a range it has
 * just built, so assigning in place is safe and spares a copy per range.
 *
 * The free range helpers below take `file` as a required parameter rather than
 * an optional one: a range built without it would silently lose the sheet's
 * identity, and this way a new call site cannot forget to pass it.
 */
function inFile(range: SourceRange, file: string | undefined): SourceRange {
  if (file !== undefined) range.file = file;
  return range;
}

/**
 * The parse diagnostics of a sheet handed in as a string next to a `.krs`
 * string, with their positions removed. Such a compile has two documents and a
 * path for neither, so a position could only be read against the `.krs`,
 * which is exactly the misreading #2715 removed; the rule that an absent
 * `file` means the consumer's own document holds only for one document. The
 * message still says what is wrong. Callers with paths go through the
 * ImportResolver, where the sheet is named.
 */
export function unplacedStyleDiagnostics(diagnostics: readonly Diagnostic[]): Diagnostic[] {
  return diagnostics.map((d) => {
    if (d.loc === undefined) return d;
    const { loc: _loc, ...rest } = d;
    return rest as Diagnostic;
  });
}

function tokenLoc(token: Token, file: string | undefined): SourceRange {
  const len = token.value.length;
  return inFile(
    {
      start: { ...token.loc },
      end: {
        line: token.loc.line,
        column: token.loc.column + len,
        offset: token.loc.offset + len,
      },
    },
    file,
  );
}

function rangeBetweenTokens(start: Token, end: Token, file: string | undefined): SourceRange {
  const len = end.value.length;
  return inFile(
    {
      start: { ...start.loc },
      end: {
        line: end.loc.line,
        column: end.loc.column + len,
        offset: end.loc.offset + len,
      },
    },
    file,
  );
}

/**
 * Consume the leading trivia attached to `token` and clear it on the token
 * so the same trivia is not visible to a later splitter call.
 */
function takeLeadingTrivia(token: Token): Trivia[] {
  const trivia = token.leadingTrivia ?? [];
  token.leadingTrivia = [];
  return trivia;
}

/**
 * Split `nextToken.leadingTrivia` into trivia that occurs on `boundaryLine`
 * (returned as `sameLine`) and the rest, which is left attached to the
 * token as its remaining `leadingTrivia`. The same-line slice is the
 * trailing trivia of whatever construct ended on `boundaryLine`.
 */
function splitSameLineTrivia(nextToken: Token, boundaryLine: number): { sameLine: Trivia[] } {
  const trivia = nextToken.leadingTrivia ?? [];
  if (trivia.length === 0) return { sameLine: [] };
  const sameLine: Trivia[] = [];
  const rest: Trivia[] = [];
  let stillSameLine = true;
  for (const t of trivia) {
    if (stillSameLine && t.kind !== "blank-line" && t.loc.start.line === boundaryLine) {
      sameLine.push(t);
    } else {
      stillSameLine = false;
      rest.push(t);
    }
  }
  nextToken.leadingTrivia = rest;
  return { sameLine };
}

export function computeSpecificity(selector: Omit<StyleSelector, "loc">): number {
  let score = 0;
  if (selector.id) score += 100;
  if (selector.edgeId) score += 100;
  // An id is an id whichever space it names, so `boundary#pci` lands at 101 by
  // the same 100 + 1 the `edge#<id>` form is built from (#2234).
  if (selector.boundaryId) score += 100;
  score += selector.tags.length * 10;
  score += selector.annotations.length * 10;
  // `from=` / `to=` endpoint predicates score like a tag (10 each), so
  // `edge[from=X]` lands at 11 — same tier as `edge[tag]`.
  if (selector.edgeFrom !== undefined) score += 10;
  if (selector.edgeTo !== undefined) score += 10;
  // `[facets=<id>]` scores like a tag too (#2175). Deliberate: a facet
  // selector is the migration target for an arbitrary-name tag selector, so
  // rewriting `[pci] { … }` as `[facets=pci] { … }` must not change which rule
  // wins in a sheet that mixes the two during the migration.
  score += selector.facets.length * 10;
  if (selector.nodeType) score += 1;
  return score;
}
